import { useId, useRef, useState } from "react";
import { adoptConnect, connectDrift, describeEntries, linkToConnect, type HoursOfOperationDetail, type HoursSchedule, type ProfileField } from "@starsach/shared-types";
import { Hint } from "../../../components/Hint";
import { Card, StatusPill, btnSecondary, inputClass } from "../../../components/ui";
import { describeHoursNow, hoursNow } from "../../../lib/hours";
import { TIME_ZONES, isKnownTimeZone } from "../../../lib/timezones";
import { AnnouncementRef, HoursEditor } from "../../customer/ProfileFieldEditors";
import { describeOverride } from "../EmergencyClose";
import { AddField, OverrideRow } from "../parts";
import type { InventoryResponse } from "@starsach/shared-types";
import { TOOLTIPS } from "../../../lib/tooltips";

type HoursField = Extract<ProfileField, { type: "hours" }>;
type Window = HoursSchedule["weekly"][number];

const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const ORDER = [1, 2, 3, 4, 5, 6, 0];
const STEP = 15;
const DAY_MIN = 24 * 60;

const toMin = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
const toHHMM = (m: number) => `${String(Math.floor(Math.min(m, DAY_MIN - 1) / 60)).padStart(2, "0")}:${String(Math.min(m, DAY_MIN - 1) % 60).padStart(2, "0")}`;
/** A point on the day, to the quarter hour. */
const snap = (m: number) => Math.max(0, Math.min(DAY_MIN, Math.round(m / STEP) * STEP));
/** A distance dragged, to the quarter hour - signed, so a window can move left (the first walk's finding). */
export const snapDelta = (m: number) => Math.round(m / STEP) * STEP;

/** Where a window lands after a drag of `delta` minutes from where it was when the drag began; never off the day, never inside out. */
export function dragged(d: { mode: "move" | "opens" | "closes"; opens: number; closes: number }, delta: number): { opens?: string; closes?: string } {
  if (d.mode === "move") {
    const length = d.closes - d.opens;
    const opens = Math.max(0, Math.min(DAY_MIN - length, d.opens + delta));
    return { opens: toHHMM(opens), closes: toHHMM(opens + length) };
  }
  if (d.mode === "opens") return { opens: toHHMM(Math.max(0, Math.min(d.closes - STEP, d.opens + delta))) };
  return { closes: toHHMM(Math.max(d.opens + STEP, Math.min(DAY_MIN, d.closes + delta))) };
}

/**
 * Hours of operation (the owner's ask of 2026-09-18): each day a bar of
 * the twenty-four hours with one or more windows on it - drag a window to
 * move it, its edges to open earlier or close later, click empty space
 * to add one (a lunch closure is two windows) - or switch to the list the
 * editor had before. Holidays are their own tab.
 */
export function HoursTab({ environmentId, profileId, fields, draft, inventory, overrides, announcements, writesEnabled, now: clock, onUpdate, onAdd, onRemove, can = { create: true, delete: true } }: { environmentId: string; profileId: string; fields: HoursField[]; draft: ProfileField[]; inventory: InventoryResponse | undefined; overrides: Array<{ field: string; value: unknown; startsAt: string; expiresAt?: string | undefined; setBy: string; reason: string }>; announcements: string[]; /** The stack reported EnableHoursOfOperationWrites: what is published here is written into Connect. */ writesEnabled: boolean; /** #484: the clock the "right now" reads, moving while the page is open. */ now: Date; onUpdate: (field: ProfileField, next: ProfileField) => void; onAdd: (field: ProfileField) => void; onRemove: (field: ProfileField) => void; /** ADR-0017: the create and delete verbs on hours; edit is the tab's own gate. */ can?: { create: boolean; delete: boolean } }) {
  const [view, setView] = useState<"bars" | "list">("bars");
  const connectHours = (inventory?.items ?? []).filter((i) => i.type === "hours_of_operation");
  return (
    <div className="grid gap-5">
      {fields.length === 0 && (
        <Card title="Hours of operation">
          <p className="text-sm text-neutral-600 dark:text-neutral-400">No hours yet. Add a schedule and the flow can ask whether you are open; holidays are authored on their own tab once there is one.</p>
        </Card>
      )}
      {fields.map((field) => {
        const override = overrides.find((o) => o.field === `hours/${field.name}`);
        const now = hoursNow(field.value, clock, override);
        const linked = field.value.connect_hours ? connectHours.find((i) => i.name === field.value.connect_hours) : undefined;
        const detail = linked?.detail;
        // #527: the windows and the zone are told apart, so a zone Connect does not have is named and can be put right on its own.
        const drift = detail ? connectDrift(field.value, detail) : undefined;
        const adopt = (d: HoursOfOperationDetail) => onUpdate(field, { ...field, value: adoptConnect(field.value, d) });
        const zone = field.value.timezone;
        return (
          <Card key={field.name} title={`Hours of operation · ${field.name}`}>
            {override && <OverrideRow environmentId={environmentId} profileId={profileId} field={`hours/${field.name}`} override={override} describe={describeOverride} />}
            {/* ADR-0019: the Connect hours of operation this schedule is; what Connect has now; adopt it, or let the next sync write over it. */}
            <div className="mb-3 flex flex-wrap items-center gap-3 rounded-lg border border-neutral-200 px-3 py-2 text-sm dark:border-neutral-800">
              <span className="flex items-center gap-2">
                <span className="text-neutral-500" aria-hidden="true">In Connect</span>
                <select
                  className={`${inputClass} mt-0 w-auto`}
                  value={field.value.connect_hours ?? ""}
                  aria-label={`Connect hours of operation for ${field.name}`}
                  // #527: linking takes Connect's zone, so the next sync does not write this schedule's zone over it.
                  onChange={(e) => onUpdate(field, { ...field, value: e.target.value ? linkToConnect(field.value, e.target.value, connectHours.find((i) => i.name === e.target.value)?.detail) : { ...field.value, connect_hours: undefined } })}
                >
                  <option value="">Not linked - the lander only</option>
                  {connectHours.map((i) => <option key={i.name} value={i.name}>{i.name}</option>)}
                  {field.value.connect_hours && !linked && <option value={field.value.connect_hours}>{field.value.connect_hours} (not in the cache)</option>}
                </select>
                <Hint id="flow.connect-hours" label="what linking does" />
              </span>
              {linked && detail && (
                <span className="flex flex-wrap items-center gap-2 text-xs text-neutral-600 dark:text-neutral-400">
                  <span>
                    now <strong>{describeEntries(detail.config)}</strong> in {detail.timezone}
                    {detail.overrides.length > 0 ? `, ${detail.overrides.length} override${detail.overrides.length === 1 ? "" : "s"}` : ""}
                  </span>
                  {drift?.timezone && (
                    <>
                      <StatusPill status="time zone differs" tone="warning" />
                      <span>this schedule reads it in {zone}</span>
                      <Hint id="flow.connect-hours.zone-drift" label="the time zone difference" />
                      <button type="button" className={`${btnSecondary} min-h-7 px-2 text-xs`} onClick={() => onUpdate(field, { ...field, value: { ...field.value, timezone: detail.timezone } })} aria-label={`Use Connect's time zone, ${detail.timezone}, for ${field.name}`}>Use Connect's time zone</button>
                    </>
                  )}
                  {drift?.windows && (
                    <>
                      <StatusPill status="differs from this schedule" tone="warning" />
                      <Hint id="flow.connect-hours.drift" label="about the difference" />
                      <button type="button" className={`${btnSecondary} min-h-7 px-2 text-xs`} onClick={() => adopt(detail)} aria-label={`Adopt Connect's hours into ${field.name}`}>Adopt Connect's hours</button>
                    </>
                  )}
                  {drift && !drift.timezone && !drift.windows && <StatusPill status="matches" tone="ok" />}
                </span>
              )}
              {linked && !detail && <span className="text-xs text-neutral-500">The cache has no detail for it yet - the next inventory push brings it.</span>}
              {linked && !writesEnabled && <span className="text-xs text-amber-800 dark:text-amber-200">The stack has not enabled hours of operation writes; publishing changes the lander's copy only.</span>}
            </div>
            <div className="mb-3 flex flex-wrap items-center gap-3 text-sm">
              <span>
                Right now <StatusPill status={describeHoursNow(now)} tone={now.open ? "ok" : "warning"} /> in {zone}
              </span>
              {/* The controls carry their own accessible names; the words beside them are for the eye. */}
              <span className="flex items-center gap-2">
                <span className="text-neutral-500" aria-hidden="true">{linked ? "Time zone, as in Connect" : "Time zone"}</span>
                <TimeZoneSelect value={zone} label={`Time zone for ${field.name}`} onChange={(timezone) => onUpdate(field, { ...field, value: { ...field.value, timezone } })} />
                {linked ? <Hint id="flow.hours.zone.linked" label="the time zone" /> : <Hint id="flow.hours.zone.unlinked" label="the time zone" />}
              </span>
              <span className="flex items-center gap-2">
                <span className="text-neutral-500" aria-hidden="true">When closed, play</span>
                <span className="block w-56">
                  <AnnouncementRef value={field.value.closed_announcement ?? ""} announcements={announcements} label={`Closed announcement for ${field.name}`} onChange={(a) => onUpdate(field, { ...field, value: { ...field.value, ...(a ? { closed_announcement: a } : { closed_announcement: undefined }) } })} />
                </span>
              </span>
              <div role="group" aria-label="View" className="ml-auto inline-flex overflow-hidden rounded-lg border border-neutral-300 text-xs dark:border-neutral-700">
                <button type="button" aria-pressed={view === "bars"} className={`min-h-8 px-3 ${view === "bars" ? "bg-accent-soft font-semibold text-accent-strong dark:bg-neutral-800 dark:text-white" : "bg-white dark:bg-neutral-900"}`} onClick={() => setView("bars")}>Bars</button>
                <button type="button" aria-pressed={view === "list"} className={`min-h-8 px-3 ${view === "list" ? "bg-accent-soft font-semibold text-accent-strong dark:bg-neutral-800 dark:text-white" : "bg-white dark:bg-neutral-900"}`} onClick={() => setView("list")}>List</button>
              </div>
              {can.delete && <button type="button" className="text-xs text-neutral-500 underline hover:text-ink dark:hover:text-white" onClick={() => onRemove(field)} aria-label={`Remove ${field.name}`}>Remove</button>}
            </div>
            {view === "bars" ? (
              <WeekBars name={field.name} timeZone={zone} weekly={field.value.weekly} onChange={(weekly) => onUpdate(field, { ...field, value: { ...field.value, weekly } })} />
            ) : (
              <HoursEditor field={field} onChange={(value) => onUpdate(field, { ...field, value: { ...value, holidays: field.value.holidays } })} windowsOnly />
            )}
            <p className="mt-3 text-xs text-neutral-500">
              Evaluated in your own account at call time, in {zone}
              {linked ? `, the zone Connect reads ${linked.name} in` : " - this schedule's own zone, since it is not linked to Connect; a new schedule starts in yours, from Account › Preferences"}. A window that closes before it opens runs past midnight; use the list view for one of those. In Connect, the stack's <em>Check hours</em>, <em>Check holiday</em> and <em>Check emergency close</em> flow modules play this decision - see <a href="/docs/deploy-guide#using-the-flow-modules" className="underline">Using the flow modules</a>.
            </p>
          </Card>
        );
      })}
      {can.create && <AddField existing={draft} inventory={inventory} onAdd={onAdd} only={["hours"]} connectHours={connectHours.map((i) => ({ name: i.name, ...(i.detail ? { detail: i.detail } : {}) }))} />}
    </div>
  );
}

/** Seven bars, one per day; windows are draggable and every edge is a keyboard-adjustable control too. */
export function WeekBars({ name, timeZone, weekly, onChange }: { name: string; /** The schedule's zone, named on the surface (#527). */ timeZone: string; weekly: Window[]; onChange: (weekly: Window[]) => void }) {
  const id = useId();
  const update = (index: number, next: Partial<Window>) => onChange(weekly.map((w, i) => (i === index ? { ...w, ...next } : w)));
  const remove = (index: number) => onChange(weekly.filter((_, i) => i !== index));
  const add = (day: number, atMinutes: number) => {
    const opens = snap(Math.max(0, atMinutes - 30));
    const closes = Math.min(DAY_MIN - STEP, opens + 60);
    onChange([...weekly, { day, opens: toHHMM(opens), closes: toHHMM(closes) }]);
  };
  return (
    <div className="grid gap-1.5" role="group" aria-label={`Weekly hours for ${name}`}>
      <p className="text-xs text-neutral-500">Times in {timeZone}</p>
      <div className="ml-24 flex justify-between text-[10px] text-neutral-500" aria-hidden="true">
        {[0, 6, 12, 18, 24].map((h) => <span key={h}>{String(h).padStart(2, "0")}:00</span>)}
      </div>
      {ORDER.map((day) => (
        <DayBar key={day} id={`${id}-${day}`} day={day} windows={weekly.map((w, index) => ({ ...w, index })).filter((w) => w.day === day)} onAdd={(m) => add(day, m)} onUpdate={update} onRemove={remove} />
      ))}
    </div>
  );
}

function DayBar({ id, day, windows, onAdd, onUpdate, onRemove }: { id: string; day: number; windows: Array<Window & { index: number }>; onAdd: (minutes: number) => void; onUpdate: (index: number, next: Partial<Window>) => void; onRemove: (index: number) => void }) {
  const track = useRef<HTMLDivElement>(null);
  const drag = useRef<{ index: number; mode: "move" | "opens" | "closes"; startX: number; opens: number; closes: number } | null>(null);

  const minutesAt = (clientX: number) => {
    const rect = track.current!.getBoundingClientRect();
    return snap(((clientX - rect.left) / rect.width) * DAY_MIN);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d) return;
    const delta = snapDelta(((e.clientX - d.startX) / track.current!.getBoundingClientRect().width) * DAY_MIN);
    onUpdate(d.index, dragged(d, delta));
  };
  const start = (e: React.PointerEvent, index: number, mode: "move" | "opens" | "closes", w: Window) => {
    e.stopPropagation();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    drag.current = { index, mode, startX: e.clientX, opens: toMin(w.opens), closes: toMin(w.closes) };
  };
  const end = () => {
    drag.current = null;
  };
  const nudge = (index: number, w: Window, edge: "opens" | "closes", delta: number) => {
    const o = toMin(w.opens);
    const c = toMin(w.closes);
    if (edge === "opens") onUpdate(index, { opens: toHHMM(Math.max(0, Math.min(c - STEP, o + delta))) });
    else onUpdate(index, { closes: toHHMM(Math.max(o + STEP, Math.min(DAY_MIN, c + delta))) });
  };
  const keys = (index: number, w: Window, edge: "opens" | "closes") => (e: React.KeyboardEvent) => {
    if (e.key === "ArrowLeft" || e.key === "ArrowDown") { e.preventDefault(); nudge(index, w, edge, -STEP); }
    if (e.key === "ArrowRight" || e.key === "ArrowUp") { e.preventDefault(); nudge(index, w, edge, STEP); }
    if (e.key === "Delete" || e.key === "Backspace") { e.preventDefault(); onRemove(index); }
  };

  return (
    <div className="flex items-center gap-2">
      <span className="w-22 flex-none text-xs text-neutral-600 dark:text-neutral-400" id={`${id}-label`}>{DAYS[day]}</span>
      <div
        ref={track}
        role="presentation"
        className="relative h-9 flex-1 cursor-copy rounded-md bg-neutral-100 dark:bg-neutral-800"
        onClick={(e) => { if (e.target === track.current) onAdd(minutesAt(e.clientX)); }}
        onPointerMove={onPointerMove}
        onPointerUp={end}
        onPointerCancel={end}
        title={TOOLTIPS["flow.hours.add-window"]}
      >
        {[6, 12, 18].map((h) => <span key={h} aria-hidden="true" className="absolute inset-y-0 w-px bg-neutral-300/70 dark:bg-neutral-700" style={{ left: `${(h / 24) * 100}%` }} />)}
        {windows.map((w) => {
          const o = toMin(w.opens);
          const c = toMin(w.closes);
          const wraps = c <= o;
          const left = (o / DAY_MIN) * 100;
          const width = ((wraps ? DAY_MIN - o : c - o) / DAY_MIN) * 100;
          return (
            <div
              key={w.index}
              className="absolute inset-y-1 flex items-center rounded-md border-[1.5px] border-accent-strong bg-accent-soft text-[11px] text-ink dark:bg-neutral-700 dark:text-white"
              style={{ left: `${left}%`, width: `${Math.max(width, 2)}%` }}
              onPointerDown={(e) => start(e, w.index, "move", w)}
              role="group"
              aria-label={`${DAYS[day]} ${w.opens} to ${w.closes}`}
            >
              <button type="button" className="h-full w-2.5 flex-none cursor-ew-resize rounded-l-md bg-accent-strong/70 hover:bg-accent-strong" aria-label={`${DAYS[day]} opens at ${w.opens}; arrow keys move it by 15 minutes`} onPointerDown={(e) => start(e, w.index, "opens", w)} onKeyDown={keys(w.index, w, "opens")} />
              <span className="min-w-0 flex-1 cursor-grab select-none truncate px-1 text-center tabular-nums">{w.opens}–{wraps ? "24:00+" : w.closes}</span>
              <button type="button" className="text-neutral-500 hover:text-red-700 dark:hover:text-red-300" aria-label={`Remove ${DAYS[day]} ${w.opens} to ${w.closes}`} onPointerDown={(e) => e.stopPropagation()} onClick={() => onRemove(w.index)}>×</button>
              <button type="button" className="h-full w-2.5 flex-none cursor-ew-resize rounded-r-md bg-accent-strong/70 hover:bg-accent-strong" aria-label={`${DAYS[day]} closes at ${w.closes}; arrow keys move it by 15 minutes`} onPointerDown={(e) => start(e, w.index, "closes", w)} onKeyDown={keys(w.index, w, "closes")} />
            </div>
          );
        })}
      </div>
      <button type="button" className={`${btnSecondary} min-h-7 px-2 text-xs`} aria-label={`Add a window on ${DAYS[day]}`} onClick={() => onAdd(9 * 60 + 30)}>+</button>
    </div>
  );
}

/** The tz database's ids, flat and alphabetical as Connect's Hours of operation page lists them; the value is always one of them because nothing else can be picked. */
export function TimeZoneSelect({ value, label, onChange }: { value: string; label: string; onChange: (zone: string) => void }) {
  const known = isKnownTimeZone(value);
  return (
    <select className={`${inputClass} mt-0 w-64`} value={value} aria-label={label} onChange={(e) => onChange(e.target.value)}>
      {!known && <option value={value}>{value || "Choose a time zone"}</option>}
      {TIME_ZONES.map((z) => <option key={z} value={z}>{z}</option>)}
    </select>
  );
}
